import {
  useTogglePlanMutation,
} from "@features/onboarding/utility-setup/hooks";
import { useClonePlan, usePlanList } from "../../hooks";
import {
  planFilters,
  PlanListItem,
} from "@features/onboarding/utility-setup/types";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Switch } from "@shared/ui/switch";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Copy, Edit, Eye, MoreHorizontal } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FilterPanel } from "./Filter";
import { Badge } from "@shared/ui/badge";
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { EditPlaneModal } from "../detail/EditRateModal";
import { TableAction } from "@shared/types/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";

const PlanList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();
  const itemsPerPage = PAGE_SIZE;
  const togglePlanMutation = useTogglePlanMutation();

  const getFiltersFromUrl = () => {
    const page = parseInt(searchParams.get("page") || "1");
    const query = searchParams.get("query") || "";
    const category = searchParams.get("category") || "";
    const utilityService = searchParams.get("utilityService") || "";
    return {
      page,
      limit: itemsPerPage,
      remote_utility_id: Number(remoteUtilityId),
      search_data: query, // Add search_data from URL query param
      category: category,
      utilityService: utilityService,
    };
  };

  const [filters, setFilters] = useState(getFiltersFromUrl);

  useEffect(() => {
    setFilters(getFiltersFromUrl());
  }, [searchParams, remoteUtilityId]);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [planToToggle, setPlanToToggle] = useState<PlanListItem | null>(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPlanForEdit, setSelectedPlanForEdit] = useState<PlanListItem | null>(null);

  const handleToggleClick = (row: PlanListItem) => {
    setPlanToToggle(row);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmToggle = () => {
    if (planToToggle) {
      handleStatusToggle(planToToggle);
      setIsConfirmModalOpen(false);
      setPlanToToggle(null);
    }
  };

  const clonePlanMutation = useClonePlan();

  const handleClone = async (row: PlanListItem) => {
    try {
      await clonePlanMutation.mutateAsync(String(row.id));
      queryClient.refetchQueries({ queryKey: QueryKeyFactory.module.onboarding.planTarrif.planTable().slice(0, 3)});
      
      // Wait for clone to finish, then maybe refetch
      // The useSmartMutation may handle query invalidation
    } catch (e) {
      console.error("Failed to clone plan", e);
    }
  };

  const {
    data,
    isLoading: loading,
    error,
  } = usePlanList(filters, remoteUtilityId);

  const onEdit = (row: PlanListItem) => {
    const stepMap: Record<number, string> = {
      1: "basic-details",
      2: "consumer-categories",
      3: "utility-services",
      4: "service-charges",
    };

    const navigationStepIndex = (row.currentStep ?? 0) + 1;

    const navigationStep = stepMap[navigationStepIndex];
    console.log("navigationStep", navigationStep);

    console.log("row", row, "→ navigating to:", navigationStep);
    const actionPath = row.statusDisplay?.toUpperCase() === 'DRAFT' ? 'draftedit' : 'edit';
    navigate(`/plan-tarrif/plan/${actionPath}/${row.id}#${navigationStep}`, {
      state: { initialData: row },
    });
  };

  const onView = (row: PlanListItem) => {
    console.log("row", JSON.stringify(row, null, 2));
    navigate(`/plan-tarrif/plan/view/${row.id}/?tab=details`, {
      state: { initialData: row },
    });
  };

  const columns: TableColumn<PlanListItem>[] = [
    {
      key: "planName",
      header: "Plan Name",
      width: "w-64",
      render: (value) => (
        <span className="text-sm font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "categoriesResolved",
      header: "Category",
      width: "w-48",
      render: (value) => {
        const categoryArray = Array.isArray(value) ? value : [];
        if (!categoryArray || categoryArray.length === 0) return <span className="text-sm text-gray-500">-</span>;
        
        const displayCategories = categoryArray.slice(0, 2);
        const remainingCount = categoryArray.length - 2;

        return (
          <div className="flex flex-wrap gap-1 items-center">
            {displayCategories.map((cat, idx) => (
              cat?.name ? (
                <Badge key={idx} variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 font-normal">
                  {cat.name}
                </Badge>
              ) : null
            ))}
            {remainingCount > 0 && (
              <TooltipProvider>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <span className="inline-block cursor-help">
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 font-normal">
                        +{remainingCount} more
                      </Badge>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex flex-col gap-1">
                      {categoryArray.map((cat, idx) => (
                        cat?.name ? <span key={idx} className="text-sm">{cat.name}</span> : null
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
    },
    {
      key: "billingFrequencyDisplay",
      header: "Frequency",
      width: "w-40",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "version",
      header: "Version",
      
    },
    {
      key: "createdDate",
      header: "Created On",
      render: (value, row) => (
        <div>
          <p className="font-medium">{value ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.createdUserRemoteName ?? "NA"}
          </p>
        </div>
      ),
    },
    {
      key: "validity",
      header: "Validity",
      width: "w-40",
      render: (value) => (
        <span className="text-sm text-gray-700">{value ?? "Ongoing"}</span>
      ),
    },
    {
      key: "utilityService",
      header: "Utility Service",
      width: "w-64",
      render: (services) => {
        const servicesArray = Array.isArray(services) ? services : [services];
        return (
          <div className="flex flex-wrap gap-1">
            {servicesArray.map((service, idx) => (
              service ? <Badge key={idx} variant="secondary">{service as string}</Badge> : null
            ))}
          </div>
        );
      },
    },
    {
      key: "statusDisplay",
      header: "Status",
      width: "w-36",
      render: (value, row) => (
        <Badge variant={row.statusDisplay?.toUpperCase() === "ACTIVE" ? "success" : "secondary"}>
          {value || row.statusDisplay}
        </Badge>
      ),
    },
  ];

  const actions: TableAction<PlanListItem>[] = [
    {
      label: "View",
      icon: <Eye className="h-4 w-4" />,
      onClick: (row) => onView(row),
      hidden: (row) => row.statusDisplay?.toUpperCase() === "DRAFT",
    },
    {
      label: "Edit",
      icon: <Edit className="h-4 w-4" />,
      onClick: (row) => {
        setSelectedPlanForEdit(row);
        setIsEditModalOpen(true);
      },
      hidden: (row) => row.statusDisplay?.toUpperCase() === "DRAFT",
    },
    {
      label:"Continue to draft",
      hidden: (row) => row.statusDisplay?.toUpperCase() !== "DRAFT",
      onClick: (row) => onEdit(row),
    },
    {
      label: "Clone",
      icon: <Copy className="h-4 w-4" />,
      onClick: (row) => { handleClone(row); },
      hidden: (row) => row.statusDisplay?.toUpperCase() === "DRAFT",
    },
    {
      label: "Make Inactive",
      hidden: (row) => row.statusDisplay?.toUpperCase() === "INACTIVE" || row.statusDisplay?.toUpperCase() === "DRAFT",
      onClick: (row) => handleToggleClick(row),
    },
    {
      label: "Make Active",
      hidden: (row) => row.statusDisplay?.toUpperCase() === "ACTIVE" || row.statusDisplay?.toUpperCase() === "DRAFT",
      onClick: (row) => handleToggleClick(row),
    },
  ];

  // Handle user status toggle
  const handleStatusToggle = (item: any) => {
    const payload = {
      id: item.id,
      status:item.statusDisplay?.toUpperCase() === "ACTIVE"? 6 : 5,
      "is_bulk_update":1,
    };

    togglePlanMutation.mutate(payload);
  };

  const handleApplyFilters = (newFilters: Partial<planFilters>) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("status");
    params.delete("department");
    params.delete("role");
    params.delete("page");

    if (newFilters.category) {
      params.set("category", newFilters.category);
    }
    if (newFilters.utilityService) {
      params.set("utilityService", newFilters.utilityService);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("status");
    params.delete("department");
    params.delete("role");
    params.delete("page");
    params.delete("query");
    params.delete("utilityService");
    params.delete("category");

    setSearchParams(params);
  };

  const handleSearch = (searchTerm: string, paramName: string) => {
    const params = new URLSearchParams(searchParams);
    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
      params.set("page", "1"); // Reset to first page when searching
    } else {
      params.delete(paramName);
    }
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const currentPage = parseInt(searchParams.get("page") || "1");

  return (
    <div>
      {/* <Button size="sm" className="gap-1 float-right" onClick={handleAdd}>
        <Plus className="h-4 w-4" /> Add Plan
      </Button> */}
      <div className="space-y-4">
        <GenericTable
          data={data?.result || []}
          columns={columns}
          searchPlaceholder="Search plans..."
          searchParamName="query"
          searchDebounceMs={300}
          onSearch={handleSearch}
          rowKey="id"
          pagination={{
            currentPage,
            totalPages: Math.ceil((data?.count || 0) / itemsPerPage),
            totalItems: data?.count || 0,
            itemsPerPage,
            startItem: (currentPage - 1) * itemsPerPage + 1,
            endItem: Math.min(currentPage * itemsPerPage, data?.count || 0),
          }}
          onPageChange={handlePageChange}
          actions={actions}
          actionsType="dropdown"
          filterPanel={
            <FilterPanel
              currentFilters={filters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
            />
          }
          emptyMessage="No plans found"
        />
      </div>

      <DeleteConfirmationDialog
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmToggle}
        title={planToToggle?.statusDisplay?.toUpperCase() === "ACTIVE" ? "Deactivate Plan" : "Activate Plan"}
        description={
          planToToggle?.statusDisplay?.toUpperCase() === "ACTIVE"
            ? `Are you sure you want to make "${planToToggle.planName}" inactive?`
            : `Are you sure you want to make "${planToToggle?.planName}" active?`
        }
        confirmText={planToToggle?.statusDisplay?.toUpperCase() === "ACTIVE" ? "Deactivate" : "Activate"}
        isLoading={togglePlanMutation.isPending}
      />

      <EditPlaneModal 
        isOpen={isEditModalOpen} 
        onOpenChange={setIsEditModalOpen} 
        currentVersion={selectedPlanForEdit?.version || "1.0"} 
        planId={selectedPlanForEdit?.id ? String(selectedPlanForEdit.id) : undefined}
      />
    </div>
  );
};

export default PlanList;
